/**
 * Graphics quality: five tiers (คุณภาพกราฟิก in ตั้งค่า, graphics.setting.ts), each a preset of the
 * visual settings plus what only a tier sets — the characters' pixel density (render/density.ts),
 * the screen's sharpness (device pixel ratio), how many figures are drawn, whose cloth streams and
 * how many particles fly.
 *
 * - ต่ำมาก: characters 1×; lighting, ambience, shake, flash and blur off; DPR ≤ 1 (× 0.75 on a
 *   phone); crowd 20 (the farthest hidden); cloth on the player only; 300 particles.
 * - ต่ำ: 1×; lighting, shake and flash on, ambience and blur off; DPR ≤ 1; crowd 40; cloth as before
 *   (everyone's till a crowd); 600 particles.
 * - ปานกลาง: 2× (option C); all on but blur, ambience halved; DPR ≤ 1.5; crowd 60; cloth as before; 900.
 * - สูง: 2×; all on; DPR ≤ 2; crowd 100; cloth also on NPCs near the player in a crowd; 1500.
 * - สูงมาก: 2×; all on; DPR ≤ 3; no LOD; cloth on everyone; 3000 particles.
 *
 * The preset is applied when a tier is picked; the player can still turn any of its settings on or
 * off afterwards, and the menu then says "กำหนดเอง". Everything applies at once except the
 * characters' density, which needs the other bake set and so applies after a reload.
 *
 * On the first run the tier is guessed from the device (`guessTier`: memory, the GPU's name, the
 * screen and the pointer), checked with a 60-frame probe behind the loading scene (`probeTier`),
 * and saved; during the first two minutes of play a slow frame rate brings a suggestion to lower it
 * (`watchFrames`), never a change. `?gfx=0…4` in the URL (dev, or `?gm`) forces a tier for the page.
 */
import { setDensity, density, type Density } from './render/density';
import { TIER_NAMES } from './graphics.setting';
import { onSettingsReset, saveSettings, settings, type Settings } from './settings';

export type TierIndex = 0 | 1 | 2 | 3 | 4;
const TOP: TierIndex = 4;

/** The visual settings a tier presets (each still the player's to change). */
type Preset = Pick<Settings, 'lighting' | 'ambience' | 'shake' | 'flash' | 'uiBlur'>;
const PRESET_KEYS = [
  'lighting',
  'ambience',
  'shake',
  'flash',
  'uiBlur',
] as const satisfies readonly (keyof Preset)[];

export interface Tier {
  /** The characters' pixel density (render/density.ts): applies after a reload. */
  density: Density;
  preset: Preset;
  /** The share of the ambience (petals, mist) drawn while it is on (the low tiers' preset has it off). */
  ambience: number;
  /** The most device pixels per CSS pixel the screen is drawn at. */
  dpr: number;
  /** The render scale on a phone-sized screen (the canvas drawn smaller and stretched). */
  phoneScale: number;
  /** The most figures drawn besides the player, their target and monsters; the farthest go first. */
  crowd: number;
  /** Whose cloth streams: the player's only, as before (everyone's till a crowd), also NPCs near the player, or everyone's. */
  cloth: 'player' | 'crowd' | 'near' | 'all';
  /** The most particles alive at once. */
  particles: number;
}

const ALL_ON: Preset = { lighting: true, ambience: true, shake: true, flash: true, uiBlur: true };
export const TIERS: readonly Tier[] = [
  {
    density: '1x',
    preset: { lighting: false, ambience: false, shake: false, flash: false, uiBlur: false },
    ambience: 0.5,
    dpr: 1,
    phoneScale: 0.75,
    crowd: 20,
    cloth: 'player',
    particles: 300,
  },
  {
    density: '1x',
    preset: { lighting: true, ambience: false, shake: true, flash: true, uiBlur: false },
    ambience: 0.5,
    dpr: 1,
    phoneScale: 1,
    crowd: 40,
    cloth: 'crowd',
    particles: 600,
  },
  {
    density: 'c',
    preset: { ...ALL_ON, uiBlur: false },
    ambience: 0.5,
    dpr: 1.5,
    phoneScale: 1,
    crowd: 60,
    cloth: 'crowd',
    particles: 900,
  },
  {
    density: 'c',
    preset: ALL_ON,
    ambience: 1,
    dpr: 2,
    phoneScale: 1,
    crowd: 100,
    cloth: 'near',
    particles: 1500,
  },
  {
    density: 'c',
    preset: ALL_ON,
    ambience: 1,
    dpr: 3,
    phoneScale: 1,
    crowd: Infinity,
    cloth: 'all',
    particles: 3000,
  },
];

/** The first run's detection, kept per browser (settings keep the tier itself). */
const KEY = 'nine-swords.graphics.v1';
interface Detected {
  tier: TierIndex;
  /** The guess before the probe, and why. */
  guess: TierIndex;
  why: string[];
  gpu: string;
  /** The probe's p95 frame time (ms), once it ran. */
  p95?: number;
}

const clampTier = (n: number): TierIndex => Math.max(0, Math.min(TOP, Math.round(n))) as TierIndex;

/** `?gfx=` (dev builds, or `?gm`): a tier forced for this page, not saved. */
function forced(): TierIndex | null {
  if (typeof location === 'undefined') return null;
  const q = new URLSearchParams(location.search);
  const v = q.get('gfx');
  if (v === null || !(import.meta.env.DEV || q.has('gm'))) return null;
  const n = Number(v);
  return Number.isFinite(n) ? clampTier(n) : null;
}

let override: TierIndex | null = null;
/** This load's detection, if the tier was guessed now (the first run): the probe may lower it. */
let fresh: Detected | null = null;
const listeners: (() => void)[] = [];

/** The tier in use. */
export const tierIndex = (): TierIndex => override ?? clampTier(settings.graphics);
export const tier = (): Tier => TIERS[tierIndex()]!;

/** Called whenever the tier or its preset changes (to resize the screen, re-apply the blur). */
export function onGraphicsChange(f: () => void): void {
  listeners.push(f);
}
const changed = () => {
  for (const f of listeners) f();
};

/**
 * Picks a tier: its preset applied (all of it, or with `keepChanged` only the settings the player
 * never changed from their default), saved, and applied at once (but the density; see above).
 */
export function applyTier(i: number, keepChanged = false): void {
  const t = clampTier(i);
  settings.graphics = t;
  const p = TIERS[t]!.preset;
  for (const k of PRESET_KEYS) if (!keepChanged || settings[k] === ALL_ON[k]) settings[k] = p[k];
  saveSettings();
  changed();
}

/** Whether the visual settings differ from the tier's preset ("กำหนดเอง"). */
export const isCustom = (): boolean => PRESET_KEYS.some((k) => settings[k] !== tier().preset[k]);
/** Whether the tier picked draws characters at another density than this page does (needs a reload). */
export const needsReload = (): boolean => TIERS[clampTier(settings.graphics)]!.density !== density();

/** The line beside คุณภาพกราฟิก in ตั้งค่า. */
export function graphicsNote(): string | null {
  const notes: string[] = [];
  if (override !== null) notes.push(`ทดสอบ: ${TIER_NAMES[override]}`);
  if (isCustom()) notes.push('กำหนดเอง');
  if (needsReload()) notes.push('ความละเอียดตัวละครจะเปลี่ยนหลังโหลดเกมใหม่');
  return notes.length ? notes.join(' · ') : null;
}

function readDetected(): Detected | null {
  try {
    const d = JSON.parse(localStorage.getItem(KEY) ?? 'null') as Detected | null;
    return d && typeof d.tier === 'number' ? d : null;
  } catch {
    return null;
  }
}
function writeDetected(d: Detected): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(d));
  } catch {
    // Storage blocked: detected again next time.
  }
}

/** The GPU's name (the unmasked renderer string where the browser gives it), or ''. */
function gpuName(): string {
  try {
    const c = document.createElement('canvas');
    const gl = (c.getContext('webgl') ?? c.getContext('experimental-webgl')) as WebGLRenderingContext | null;
    if (!gl) return '';
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    const name = String(gl.getParameter(ext ? ext.UNMASKED_RENDERER_WEBGL : gl.RENDERER) ?? '');
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return name;
  } catch {
    return '';
  }
}

/** A phone-sized screen: its short side under 500 CSS pixels. */
const phone = (): boolean => typeof screen !== 'undefined' && Math.min(screen.width, screen.height) < 500;

/**
 * A first guess at the tier from the device, highest it might manage first, then capped:
 * - the GPU's name: software (SwiftShader, llvmpipe, Microsoft Basic) → ต่ำมาก; old mobile and
 *   integrated (Mali-4xx/T6xx/T7xx, Adreno 3xx–5xx, PowerVR, Intel HD) → ต่ำ; strong (Apple M-series
 *   and Apple GPUs, Adreno 6xx+, GeForce, Radeon, Arc) → สูง, and สูงมาก on a screen at least 2560
 *   device pixels wide; unknown → ปานกลาง;
 * - `navigator.deviceMemory`: ≤ 2 GB → ต่ำมาก at most, ≤ 4 GB → ต่ำ at most (Chrome only);
 * - a phone-sized screen with a coarse pointer → ปานกลาง at most (a high DPR costs fill rate).
 */
export function guessTier(): { tier: TierIndex; why: string[]; gpu: string } {
  const why: string[] = [];
  const gpu = gpuName();
  let t: TierIndex = 2;
  if (/swiftshader|llvmpipe|softpipe|microsoft basic|software/i.test(gpu)) {
    t = 0;
    why.push('software GPU');
  } else if (/mali-(4|t6|t7)|adreno[^0-9]*[345]\d\d\b|powervr|intel.*\bhd graphics\b/i.test(gpu)) {
    t = 1;
    why.push('weak GPU');
  } else if (/apple (m\d|gpu)|adreno[^0-9]*([6-9]\d\d|\d{4})\b|geforce|nvidia|radeon|\barc\b/i.test(gpu)) {
    const wide = typeof screen !== 'undefined' && screen.width * (devicePixelRatio || 1) >= 2560;
    t = wide ? 4 : 3;
    why.push(wide ? 'strong GPU, wide screen' : 'strong GPU');
  } else why.push(gpu ? 'unknown GPU' : 'no GPU name');
  const mem = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  if (mem && mem <= 2 && t > 0) {
    t = 0;
    why.push(`${mem} GB memory`);
  } else if (mem && mem <= 4 && t > 1) {
    t = 1;
    why.push(`${mem} GB memory`);
  }
  if (phone() && matchMedia('(pointer: coarse)').matches && t > 2) {
    t = 2;
    why.push('phone screen');
  }
  return { tier: t, why, gpu };
}

/**
 * Settles the tier for this page, once at boot before anything is drawn: a forced one (`?gfx=`),
 * the saved one, or on the first run a guess (`guessTier`) whose preset is applied to the settings
 * the player never changed. Sets the characters' density from it (render/density.ts).
 */
export function startGraphics(): void {
  override = forced();
  if (override === null && !readDetected()) {
    try {
      const g = guessTier();
      fresh = { tier: g.tier, guess: g.tier, why: g.why, gpu: g.gpu };
      writeDetected(fresh);
      applyTier(g.tier, true);
    } catch (e) {
      console.warn('Graphics detection failed', e);
    }
  }
  setDensity(tier().density);
  // Put back to the defaults: the tier detected for this browser, its preset whole.
  onSettingsReset(() => {
    const t = readDetected()?.tier ?? 3;
    settings.graphics = t;
    Object.assign(settings, TIERS[t]!.preset);
    queueMicrotask(changed);
  });
}

/**
 * Frame times (ms) of the next `n` animation frames, gaps of a hidden tab left out; whatever was
 * measured after `ms` if the frames stop coming (a hidden tab), so a load never waits on it.
 */
function frameTimes(n: number, ms = 4000): Promise<number[]> {
  return new Promise((done) => {
    const out: number[] = [];
    let last = -1;
    let over = false;
    const end = () => {
      if (over) return;
      over = true;
      done(out);
    };
    const tick = (now: number) => {
      if (over) return;
      if (last >= 0 && now - last < 250) out.push(now - last);
      last = now;
      if (out.length < n) requestAnimationFrame(tick);
      else end();
    };
    requestAnimationFrame(tick);
    setTimeout(end, ms);
  });
}
const p95 = (xs: number[]): number => [...xs].sort((a, b) => a - b)[Math.floor(xs.length * 0.95)] ?? 0;

/**
 * The first run's check, behind the loading scene once the world is ready and being drawn: 60
 * frames, and the tier lowered one step if their p95 is over 25 ms, two if over 40 ms. A lower tier
 * of the other density takes over from the next load. Does nothing after the first run.
 */
export async function probeTier(): Promise<void> {
  if (!fresh) return;
  const times = await frameTimes(60);
  // Too few frames to judge (a hidden tab): the guess stands.
  if (times.length < 30) return;
  const ms = p95(times);
  const down = ms > 40 ? 2 : ms > 25 ? 1 : 0;
  fresh.p95 = Math.round(ms * 10) / 10;
  if (down) {
    fresh.tier = clampTier(fresh.tier - down);
    fresh.why.push(`probe p95 ${fresh.p95} ms`);
    applyTier(fresh.tier, true);
  }
  writeDetected(fresh);
  fresh = null;
}

/** How long into play slow frames bring a suggestion, the p95 that counts as slow, and how often to look. */
const WATCH_MS = 120_000;
const SLOW_MS = 33;
const WINDOW = 120;

/**
 * Watches the frame rate for the first two minutes of play: if a stretch of `WINDOW` frames has a
 * p95 over 33 ms, `suggest` is called once with a Thai line naming a lower tier. Never lowers it.
 * A hidden tab's gaps are not counted.
 */
export function watchFrames(suggest: (text: string) => void): void {
  let until = performance.now() + WATCH_MS;
  let times: number[] = [];
  let last = -1;
  const tick = (now: number) => {
    // Not in a bout (the HUD marks it): the toast waits for after, and the watch window is pushed on.
    if (typeof document !== 'undefined' && document.body.classList.contains('duel-on')) {
      until = Math.max(until, now + 5000);
      times = [];
      last = now;
      requestAnimationFrame(tick);
      return;
    }
    if (last >= 0 && now - last < 250) times.push(now - last);
    last = now;
    if (times.length >= WINDOW) {
      const t = tierIndex();
      if (p95(times) > SLOW_MS && t > 0) {
        suggest(`ภาพกระตุก ลองลดคุณภาพกราฟิกเป็น “${TIER_NAMES[t - 1]}” ในตั้งค่า`);
        return;
      }
      times = [];
    }
    if (now < until) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

// ---------------------------------------------------------------- what the renderer asks

/** Device pixels per CSS pixel to draw the screen at. */
export function screenDpr(): number {
  const t = tier();
  const dpr = Math.min(t.dpr, (typeof devicePixelRatio === 'number' && devicePixelRatio) || 1);
  return t.phoneScale < 1 && phone() ? dpr * t.phoneScale : dpr;
}

/** The most particles alive at once. */
export const particleCap = (): number => tier().particles;

/** The share of the ambience drawn while it is on. */
export const ambienceShare = (): number => tier().ambience;

/** Tiles from the player within which an NPC's cloth streams at สูง. */
const NEAR = 8;

/**
 * Whether a figure's cloth streams: the player's always; at ต่ำมาก no one else's; otherwise everyone's
 * till a crowd is on screen (`crowded`), and at สูง those near the player's even then, at สูงมาก all.
 */
export function clothStreams(isPlayer: boolean, crowded: boolean, tilesAway: number): boolean {
  if (isPlayer) return true;
  const c = tier().cloth;
  if (c === 'player') return false;
  if (c === 'all') return true;
  if (!crowded) return true;
  return c === 'near' && tilesAway < NEAR;
}

/**
 * The tier's figure LOD: of `items` (the frame's draw list), the figures that may be left out —
 * anyone but the player, their target, monsters and pets — kept to the
 * nearest `crowd` of them; the rest are removed from the list (in place).
 */
export function cullCrowd<T extends { ent?: { id: number; kind: string; x: number; y: number } }>(
  items: T[],
  player: { id: number; x: number; y: number; targetId: number | null },
): void {
  const cap = tier().crowd;
  if (!Number.isFinite(cap)) return;
  const spare = items.filter(
    (it) =>
      it.ent &&
      it.ent.id !== player.id &&
      it.ent.id !== player.targetId &&
      it.ent.kind !== 'monster' &&
      it.ent.kind !== 'pet',
  );
  if (spare.length <= cap) return;
  const d = (it: T) => Math.hypot(it.ent!.x - player.x, it.ent!.y - player.y);
  spare.sort((a, b) => d(a) - d(b));
  const drop = new Set(spare.slice(cap));
  let j = 0;
  for (const it of items) if (!drop.has(it)) items[j++] = it;
  items.length = j;
}
